import { ActivityTrackerEventType } from '../../activityTracker/constants';
import { useActivityTracker } from '../../activityTracker/useActivityTracker';
import { SegmentEventType } from '../../externalTracking/types';
import { useExternalTracking } from '../../externalTracking/useExternalTracking';
import { type SignupRequest } from '../aliases';
import { SignupSubmitTrackingLoginType, type SignupType } from '../types';

import { getPersistentId } from '@/modules/activityTracker/helpers';

export function useSignupTracking() {
  const { sendActivityTrackerEvent } = useActivityTracker();
  const {
    gtm: { sendEvent: sendGTMEvent },
    branch: { sendEvent: sendBranchEvent },
    segment: {
      sendEvent: sendSegmentEvent,
      identifyUser: sendSegmentIdentification,
    },
  } = useExternalTracking();

  function sendSignupViewEvent(pageName: string) {
    sendActivityTrackerEvent(ActivityTrackerEventType.SIGNUP_VIEW, {
      schemaVersion: '2.0',
      pageName,
    });
  }

  function sendSignupActionEvent({
    actionType,
    fieldName,
  }: {
    actionType: string;
    fieldName: string;
  }) {
    sendActivityTrackerEvent(ActivityTrackerEventType.SIGNUP_ACTION, {
      actionType,
      fieldName,
      schemaVersion: '3.0',
    });
  }

  function sendSignupErrorEvent(errorCode: string) {
    sendActivityTrackerEvent(ActivityTrackerEventType.SIGNUP_ERROR, {
      schemaVersion: '2.0',
      errorCode,
    });
  }

  function sendSignupSubmitActionEvent(signupType: SignupType) {
    sendActivityTrackerEvent(ActivityTrackerEventType.SIGNUP_SUBMIT_ACTION, {
      loginType: SignupSubmitTrackingLoginType[signupType],
      schemaVersion: '1.0',
    });
  }

  async function sendSignupSuccessEvents({
    userId,
    userDetails,
    signupType,
    receiveEmails,
    eventId,
    pageOrigin,
  }: {
    userId: number;
    userDetails: SignupRequest;
    signupType: SignupType;
    receiveEmails: boolean;
    eventId: string;
    pageOrigin?: string | null;
  }) {
    const persistentId = await getPersistentId();

    const segmentSignedupData = {
      userId: String(userId),
      username: userDetails.username,
      email: userDetails.email,
      method: signupType.toLowerCase(),
    };

    sendActivityTrackerEvent(
      ActivityTrackerEventType.SIGNUP_SUCCESS,
      {
        schemaVersion: '3.0',
        firstName: userDetails.first_name,
        lastName: userDetails.last_name,
        email: userDetails.email,
        username: userDetails.username,
        signupCountry: userDetails.country,
        receiveEmails,
        pageOrigin: pageOrigin ?? null,
      },
      {
        baseEventOverrides: { userId, id: eventId, schemaVersion: '6.0' },
      }
    );
    sendSegmentIdentification(String(userId), segmentSignedupData);
    sendSegmentEvent(SegmentEventType.SIGNED_UP, segmentSignedupData);

    sendGTMEvent({
      event: 'sign_up',
      method: signupType.toLowerCase(),
      country: userDetails.country,
      event_id: eventId,
    });

    await sendBranchEvent('signup confirmed', {
      user_id: String(userId),
      persistent_id: persistentId,
    });
  }

  return {
    sendSignupActionEvent,
    sendSignupViewEvent,
    sendSignupErrorEvent,
    sendSignupSubmitActionEvent,
    sendSignupSuccessEvents,
  };
}
